import { useState } from 'react';

const RequestFormSection = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    service: '',
    problem: '',
    contactMethod: 'phone',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('fullName', formData.fullName);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email);
      formBody.append('service', formData.service);
      formBody.append('problem', formData.problem);
      formBody.append('contactMethod', formData.contactMethod);

      const response = await fetch('https://readdy.ai/api/form/d58rhuuisj01hmefqvig', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          fullName: '',
          phone: '',
          email: '',
          service: '',
          problem: '',
          contactMethod: 'phone',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 lg:py-0">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Left Side - Dark Background */}
          <div className="bg-primary text-white p-8 lg:p-16 flex flex-col justify-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Need Heating or Plumbing Help?
            </h2>
            <p className="text-gray-300 text-lg mb-10 leading-relaxed">
              Get in touch with our expert team today. We respond quickly to all inquiries and provide free consultations for your plumbing and heating needs.
            </p>

            <div className="space-y-6 mb-10">
              <div className="flex items-start space-x-4 p-4 border border-white/20 rounded-lg">
                <div className="w-10 h-10 flex items-center justify-center bg-accent/20 rounded-lg flex-shrink-0">
                  <i className="ri-check-line text-accent text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">Free Consultation</h3>
                  <p className="text-gray-300 text-sm">Expert advice at no cost</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 border border-white/20 rounded-lg">
                <div className="w-10 h-10 flex items-center justify-center bg-accent/20 rounded-lg flex-shrink-0">
                  <i className="ri-time-line text-accent text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">Same-Day Service Available</h3>
                  <p className="text-gray-300 text-sm">Emergency support when you need it</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 p-4 border border-white/20 rounded-lg">
                <div className="w-10 h-10 flex items-center justify-center bg-accent/20 rounded-lg flex-shrink-0">
                  <i className="ri-price-tag-3-line text-accent text-xl"></i>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">Transparent Pricing</h3>
                  <p className="text-gray-300 text-sm">No hidden fees or surprises</p>
                </div>
              </div>
            </div>

            <p className="text-gray-400 text-sm">
              <i className="ri-time-line mr-2"></i>
              Average response time: 2 hours
            </p>
          </div>

          {/* Right Side - Form */}
          <div className="bg-white p-8 lg:p-16">
            <h3 className="text-2xl font-bold text-primary mb-6">Describe Your Problem</h3>

            <form id="client-request-form" data-readdy-form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="fullName"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent text-sm"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Phone *
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-500">
                    🇮🇪
                  </span>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent text-sm"
                    placeholder="+353 87 123 4567"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent text-sm"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                  Service Needed *
                </label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent text-sm"
                >
                  <option value="">Select a service</option>
                  <option value="Plumbing">Plumbing</option>
                  <option value="Heating">Heating</option>
                  <option value="Bathroom">Bathroom</option>
                  <option value="Emergency">Emergency</option>
                </select>
              </div>

              <div>
                <label htmlFor="problem" className="block text-sm font-medium text-gray-700 mb-2">
                  Problem Description * (Max 500 characters)
                </label>
                <textarea
                  id="problem"
                  name="problem"
                  value={formData.problem}
                  onChange={handleChange}
                  required
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent text-sm resize-none"
                  placeholder="Please describe your plumbing or heating issue..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{formData.problem.length}/500 characters</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Preferred Contact Method *
                </label>
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="contactMethod"
                      value="phone"
                      checked={formData.contactMethod === 'phone'}
                      onChange={handleChange}
                      className="w-4 h-4 text-accent focus:ring-accent"
                    />
                    <span className="ml-2 text-sm text-gray-700">Phone</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="contactMethod"
                      value="email"
                      checked={formData.contactMethod === 'email'}
                      onChange={handleChange}
                      className="w-4 h-4 text-accent focus:ring-accent"
                    />
                    <span className="ml-2 text-sm text-gray-700">Email</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="contactMethod"
                      value="whatsapp"
                      checked={formData.contactMethod === 'whatsapp'}
                      onChange={handleChange}
                      className="w-4 h-4 text-accent focus:ring-accent"
                    />
                    <span className="ml-2 text-sm text-gray-700">WhatsApp</span>
                  </label>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-accent hover:bg-accent-dark text-white py-4 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <span>Send Request</span>
                    <i className="ri-send-plane-line"></i>
                  </>
                )}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg text-sm">
                  <i className="ri-check-line mr-2"></i>
                  Thank you! We'll contact you shortly.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg text-sm">
                  <i className="ri-error-warning-line mr-2"></i>
                  Something went wrong. Please try again.
                </div>
              )}

              <p className="text-xs text-gray-500 text-center">
                By submitting this form, you agree to our privacy policy.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RequestFormSection;